import { useState, useEffect, useContext } from 'react';
import ApiConstants from '../utils/ApiConstants';
import { LanguageContext } from '../context/LanguageProvider';
import { useAuthenticatedApiFetch } from '../hooks/useAuthenticatedApiFetch';
import { useMsal } from '@azure/msal-react';
import { useAppNavigation } from "../hooks/useAppNavigation";
import { formatCurrency } from '../utils/utils';

const MainPremium = ({
  client,
  product,
  productId,
  refreshTrigger,
  onPremiumChange,
  isProductActive, 
  setDefaultProductPremium
}) => {
  const { instance } = useMsal();
  const authenticatedApiFetch = useAuthenticatedApiFetch(instance);
  const { t, language } = useContext(LanguageContext);
  const { goToError } = useAppNavigation();

  const [loading, setLoading] = useState(false);
  const [mainPremium, setMainPremium] = useState(null);
  const [apiError, setApiError] = useState(false);
  const [previousPremium, setPreviousPremium] = useState(0);
  const [hasMounted, setHasMounted] = useState(false);

  const fetchMainPremium = async (source = '') => {
    setLoading(true);
    try {
      const endpoint = ApiConstants.getMainPremium(productId || product, client);
      const url = `${ApiConstants.BASE_URL}${endpoint}`;
      const result = await authenticatedApiFetch(url);

      if (result.status === 200) {
        const premiumValue = result.data.monthlyPremium || 0;

        if (!productId) {
          onPremiumChange(premiumValue, previousPremium);
          setDefaultProductPremium(premiumValue);
        }

        if (productId && isProductActive) {
          onPremiumChange(premiumValue, previousPremium);
        }

        setPreviousPremium(premiumValue);
        setMainPremium(result.data);
        setApiError(false);
      } else {
        setApiError(true);
      }
    } catch (error) {
      console.error("Premium fetch error:", error);
      setApiError(true);
    } finally {
      setLoading(false);
    }
  };

  //  Initial fetch on client/product change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchMainPremium('initial');
      setHasMounted(true);
    }, 300); //  delay 

    return () => {
      clearTimeout(timer);
      if (previousPremium > 0) {
        onPremiumChange(0, previousPremium);
      }
    };
  }, [client, product]);


  // Handle refreshTrigger changes
  useEffect(() => {
  if (!hasMounted || typeof refreshTrigger !== 'number') return;

  const timer = setTimeout(() => {
    fetchMainPremium('refreshTrigger');
  }, 300);

  return () => clearTimeout(timer);
}, [refreshTrigger]);

  // Handle product activation toggles
  useEffect(() => {
    if (productId && isProductActive && mainPremium) {
      onPremiumChange(mainPremium.monthlyPremium, previousPremium);
      setPreviousPremium(mainPremium.monthlyPremium);
    }

    if (productId && !isProductActive && previousPremium > 0) {
      onPremiumChange(0, previousPremium);
      setPreviousPremium(0);
    }
  }, [isProductActive]);

  if (apiError) {
    console.error("Failed to load Premium endpoint.");
    goToError();
    return <div className="error text-center"><p>Failed to load Premium endpoint.</p></div>;
  }

  const formattedPremium = mainPremium?.monthlyPremium?.toFixed(2);


  return (
    <div className='main-premium'>
      {loading ? (
        <div className="loading-next"><div className="loading"></div></div>
      ) : formattedPremium ? (
        <div>
          {formatCurrency(formattedPremium, language, false, 'MONEY-REAL')} {!isProductActive && productId && (
            <span className="monthly-premium-otherproducts"> / {t('month')}</span>
          )}
        </div>
      ) : (
        <div>No premium data available</div>
      )}
    </div>
  );
};

export default MainPremium;
